/**
 * Preferências da página do setor (visão, entidade, colunas da tabela, base
 * do calendário e o modo do editor do mapa), salvas no navegador por unidade
 * (tenantStorage): são do jeito de trabalhar de quem usa o tablet, não dado
 * da produção.
 */
import { useCallback, useEffect, useState } from "react";

import { getItemTenant, setItemTenant } from "../../../utils/tenantStorage";
import { MODO } from "./plantaDoSetor";

export const CHAVE_PREFERENCIAS = "pharuerp:mapa-producao:setor:preferencias";

export const ENTIDADES = Object.freeze([
  { valor: "ambiente", rotulo: "Ambientes" },
  { valor: "lote", rotulo: "Lotes" },
  { valor: "tarefa", rotulo: "Tarefas" },
  { valor: "peca", rotulo: "Peças" },
]);

/**
 * A visão MAPA entra logo depois do Kanban: as duas mostram o setor por
 * dentro (uma em colunas, a outra na planta), e as três seguintes são listas
 * do mesmo conteúdo.
 */
export const VISOES = Object.freeze([
  { valor: "kanban", rotulo: "Kanban" },
  { valor: "mapa", rotulo: "Mapa" },
  { valor: "tabela", rotulo: "Tabela" },
  { valor: "calendario", rotulo: "Calendário" },
  { valor: "timeline", rotulo: "Linha do tempo" },
]);

const BASES = ["limite", "prevista"];
const MODOS_DO_MAPA = [MODO.ORGANIZAR, MODO.SUBSETOR, MODO.POSTO];

export const PREFERENCIAS_PADRAO = Object.freeze({
  entidade: "ambiente",
  visao: "kanban",
  baseCalendario: "limite",
  // O editor sempre abre em Organizar: desenhar é o gesto que cria coisa nova,
  // e ninguém deve criar um subsetor só por tocar no mapa ao entrar.
  modoMapa: MODO.ORGANIZAR,
  colunas: Object.freeze({}),
});

const valido = (lista, valor, padrao) => (lista.some((item) => (item.valor ?? item) === valor) ? valor : padrao);

function normalizarColunas(bruto) {
  if (!bruto || typeof bruto !== "object") return {};
  const colunas = {};
  ENTIDADES.forEach(({ valor }) => {
    const lista = bruto[valor];
    if (Array.isArray(lista)) colunas[valor] = [...new Set(lista.filter((k) => typeof k === "string" && k))];
  });
  return colunas;
}

/** Preferências confiáveis a partir de qualquer coisa (JSON velho, lixo). */
export function normalizarPreferencias(bruto) {
  const origem = bruto && typeof bruto === "object" ? bruto : {};
  return {
    entidade: valido(ENTIDADES, origem.entidade, PREFERENCIAS_PADRAO.entidade),
    visao: valido(VISOES, origem.visao, PREFERENCIAS_PADRAO.visao),
    baseCalendario: valido(BASES, origem.baseCalendario, PREFERENCIAS_PADRAO.baseCalendario),
    modoMapa: valido(MODOS_DO_MAPA, origem.modoMapa, PREFERENCIAS_PADRAO.modoMapa),
    colunas: normalizarColunas(origem.colunas),
  };
}

export function lerPreferencias() {
  try {
    return normalizarPreferencias(JSON.parse(getItemTenant(CHAVE_PREFERENCIAS) || "{}"));
  } catch {
    // JSON corrompido: começa do padrão em vez de quebrar a página.
    return normalizarPreferencias(null);
  }
}

export function salvarPreferencias(preferencias) {
  setItemTenant(CHAVE_PREFERENCIAS, JSON.stringify(normalizarPreferencias(preferencias)));
}

/** Devolve [preferencias, atualizar(parcial)] — salva a cada mudança. */
export default function usePreferenciasDoSetor() {
  const [preferencias, setPreferencias] = useState(lerPreferencias);

  useEffect(() => {
    salvarPreferencias(preferencias);
  }, [preferencias]);

  const atualizar = useCallback(
    (parcial) => setPreferencias((atuais) => normalizarPreferencias({ ...atuais, ...parcial })),
    []
  );
  return [preferencias, atualizar];
}
